import { useEffect, useRef, useState } from "react";
import type { OpsApi } from "../useOps";
import { PHOSPHOR } from "../useOps";
import type { Domain } from "@/types/api";

/**
 * BottomStrip — domain breakdown, signal-rate sparkline, and uplink telemetry,
 * overlaid on the lower map. Reads counts live from the data layer.
 */
export function BottomStrip({ ops }: { ops: OpsApi }) {
  const sparkRef = useRef<HTMLCanvasElement | null>(null);
  const [, force] = useState(0);

  // refresh telemetry ~1.5s; the canvas sparkline draws imperatively
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    drawSpark(sparkRef.current, ops);
  });

  const client = ops.getClient();
  const counts: Record<Domain, number> = { maritime: 0, aircraft: 0, satellite: 0 };
  client?.objects.forEach((o) => {
    if (counts[o.domain] != null) counts[o.domain]++;
  });
  const max = Math.max(1, ...Object.values(counts));
  const doms: Array<[Domain, string, string]> = [
    ["maritime", "Maritime", `rgb(${PHOSPHOR})`],
    ["aircraft", "Aircraft", "#d6a36e"],
    ["satellite", "Satellite", `rgba(${PHOSPHOR},0.6)`],
  ];
  const pendingMaritime = ops.isLive && counts.maritime === 0;

  return (
    <div className="map-strip">
      <div className="ms-block" title="Live object counts by tracking domain">
        <div className="ms-h">Domains in view</div>
        <div className="ms-doms">
          {doms.map(([k, nm, color]) => (
            <div className="ms-dom" key={k} title={`${nm}: ${counts[k].toLocaleString()} objects tracked`}>
              <span className="nm">{nm}</span>
              <div className="bar">
                <span style={{ width: `${(counts[k] / max) * 100}%`, background: color }} />
              </div>
              <span className="v">
                {pendingMaritime && k === "maritime" ? (
                  <span style={{ color: "var(--amber)" }}>—</span>
                ) : (
                  counts[k].toLocaleString()
                )}
              </span>
            </div>
          ))}
        </div>
        {pendingMaritime && <div className="ms-note">AIS VESSEL FEED PENDING · UPSTREAM</div>}
      </div>
      <div className="ms-block" title="New signals per 2.5-minute bucket over the past hour">
        <div className="ms-h">Signal rate · 60 min</div>
        <canvas ref={sparkRef} id="spark" />
      </div>
      <div className="ms-block ms-thru" title="Real-time data connection telemetry">
        <div className="ms-h">Uplink</div>
        <div className="row">
          <span className="k">Throughput</span>
          <span className="v">{client && ops.isLive ? `${client.avgKbps().toFixed(0)} KB/s` : "—"}</span>
        </div>
        <div className="row">
          <span className="k">Last sync</span>
          <span className="v">{client?.snapshotAge() != null ? `${client!.snapshotAge()}s` : "—"}</span>
        </div>
        <div className="row">
          <span className="k">Viewport</span>
          <span className="v">GLOBAL</span>
        </div>
      </div>
    </div>
  );
}

function drawSpark(c: HTMLCanvasElement | null, ops: OpsApi) {
  if (!c || !c.clientWidth) return;
  const ctx = c.getContext("2d");
  if (!ctx) return;
  const signals = ops.getClient()?.signals ?? [];
  const now = Date.now();
  const span = 3600e3;
  const n = 24;
  const bw0 = span / n;
  const buckets = new Array(n).fill(0);
  signals.forEach((s) => {
    const age = now - (s.createdAt || now);
    if (age >= 0 && age < span) {
      const i = n - 1 - Math.floor(age / bw0);
      if (i >= 0 && i < n) buckets[i]++;
    }
  });
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = c.clientWidth;
  const h = c.clientHeight;
  c.width = w * dpr;
  c.height = h * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  const bw = w / n;
  const max = Math.max(1, ...buckets);
  for (let i = 0; i < n; i++) {
    const bh = (buckets[i] / max) * (h - 5);
    ctx.fillStyle = i === n - 1 ? "#d6a36e" : `rgba(${PHOSPHOR},0.5)`;
    ctx.fillRect(i * bw + 1, h - bh - 1, Math.max(1, bw - 2), bh);
  }
  ctx.strokeStyle = `rgba(${PHOSPHOR},0.14)`;
  ctx.beginPath();
  ctx.moveTo(0, h - 0.5);
  ctx.lineTo(w, h - 0.5);
  ctx.stroke();
}
